// Relay embed API — the paste-anywhere install path.
//
// A junior drops two lines into ANY page (any framework, any backend) and gets a
// working chat launcher. Identity is optional and upgrades in one string. This
// mirrors how Intercom / Channel.io install: a global command function with a
// pre-load queue, plus auto-boot from a settings object.
//
//   <script>window.relaySettings = { profileId: "p_your_chatroom" }</script>
//   <script async src="https://relay.paramms.com/embed.js"></script>
//
// or, programmatically (SPAs, identity that arrives after login):
//
//   Relay('boot',     { profileId: "p_x" })
//   Relay('identify', { userId: user.id })   // becomes them + merges their guest history
//   Relay('update',   { listingId: car.id }) // switch subject on navigation
//   Relay('shutdown')                        // remove the widget (e.g. on logout)

import { mount, type WidgetHandle, type MountOptions, type UserInfo } from './index.js'
import { resolveStrings } from './i18n.js'

/** Everything an embedder can pass. All optional except `profileId`. Field
 *  names DELIBERATELY mirror the React props (`ChatWidgetProps` /
 *  `MarketplaceChatProps` in react.tsx) so the same mental model — and often
 *  the same field names — carries over whether you're using React or a plain
 *  script tag. Where a name changed over time the old one still works (see
 *  the `@deprecated` notes) — this is a published package embedded on live
 *  customer sites (WordPress plugin, Shopify theme block), so nothing here is
 *  ever removed, only added to. */
export interface RelaySettings {
  /** The chatroom id (from your Relay dashboard). Required. Matches the React
   *  `profileId` prop name. `appId` is the original alias — still works. */
  profileId?: string
  /** @deprecated alias for `profileId` — kept working, `profileId` is now the
   *  documented name (matches React). */
  appId?: string
  /** Relay server URL. Defaults to the hosted relay; set for self-hosted. */
  url?: string
  apiUrl?: string
  /** IDENTITY (optional, tiered): `token` (a signed JWT from your backend) is the
   *  secure path; `userId` (any stable string you have) is the easy path; omit
   *  both for an anonymous visitor. See EMBED.md. */
  token?: string
  userId?: string
  /** Called when a signed `token` is rejected (expired): return a fresh token
   *  from your backend to renew the session without a reload. Matches the
   *  React `refreshToken` prop. Only usable from `window.relaySettings` /
   *  `Relay('boot', ...)` (a function can't be expressed as an HTML
   *  attribute) — not available via `data-relay-*`. */
  refreshToken?: () => Promise<string | null>
  /** Display info shown to agents (not identity) — matches the React
   *  `userName` / `userEmail` / `userAvatar` props. */
  userName?: string
  userEmail?: string
  userAvatar?: string
  /** @deprecated flat aliases for `userName` / `userEmail` / `userAvatar` —
   *  kept working (the shipped Shopify integration used these names nested
   *  under `user`, see `user` below, which is the fix for that; these bare
   *  top-level fields predate that and still work standalone). */
  name?: string
  email?: string
  avatar?: string
  /** Same info as `userName`/`userEmail`/`userAvatar`, as one nested object —
   *  matches `MountOptions.user` / React's internal shape exactly, and is
   *  what a server-rendered snippet (e.g. Shopify Liquid, WordPress PHP) will
   *  most naturally emit: `user: { name: "...", email: "..." }`. Takes
   *  precedence over the flat fields if both are somehow given. */
  user?: UserInfo
  /** Subject the chat is about (e.g. a marketplace listing). `listingId` is
   *  sugar for `subjectId: "listing_<id>"`. */
  subjectId?: string
  listingId?: string
  /** Context-card fields — matches React's `ChatWidget` `contextTitle` /
   *  `contextSubtitle` / `contextStatus` props (a general "here's what this
   *  conversation is about" card: an order, ticket, booking, etc). */
  contextTitle?: string
  contextSubtitle?: string
  contextStatus?: string
  /** Marketplace-card fields — matches React's `MarketplaceChat`
   *  `listingTitle` / `listingMeta` / `listingPrice` / `listingStatus` props
   *  (a specific-item card: price + status badge, e.g. "2019 Camry — $12,500
   *  — Available"). Use these OR `contextTitle`/etc — both build the same
   *  card, pick whichever vocabulary matches your use case. */
  listingTitle?: string
  listingMeta?: string
  listingPrice?: number
  listingStatus?: string
  /** @deprecated original flat names for the context/marketplace card —
   *  kept working. `contextTitle`/`listingTitle` are now the documented
   *  names (matching the two React components). */
  subjectTitle?: string
  subjectMeta?: string
  subjectPrice?: number
  subjectStatus?: string
  /** The context/marketplace card as one nested object, if you'd rather build
   *  it yourself than use the flat fields above — matches
   *  `MountOptions.subject` exactly. Takes precedence over every flat field
   *  above if given. */
  subject?: { title?: string; subtitle?: string; tags?: string[]; status?: string; ownerLabel?: string }
  /** Pre-set reply chips shown above the input — matches the React
   *  `quickReplies` prop. `window.relaySettings` / `Relay('boot', ...)` only
   *  (an array can't be expressed as a single `data-relay-*` attribute). */
  quickReplies?: string[]
  /** i18n string overrides — matches the React `i18n` prop. Same restriction
   *  as `quickReplies`: object, so JS-object form only. */
  i18n?: MountOptions['i18n']
  /** Per-tenant feature switches (default all ON). Object form only. Matches
   *  the React `features` prop. Set a flag false to disable that feature. */
  features?: MountOptions['features']
  /** Appearance. `launcher` defaults to true (a floating bubble). */
  accent?: string
  /** Secondary accent (guest bubble + send button). Object/attr form; follows
   *  `accent` when omitted. */
  accent2?: string
  /** UI language for built-in strings ('ko', 'ko-KR', …). Attr: data-relay-locale.
   *  Omit to auto-detect from the visitor's browser. */
  locale?: string
  /** Colour scheme: 'auto'|'light'|'dark' (default 'light'; 'auto' follows OS). Attr: data-relay-theme. */
  theme?: 'auto' | 'light' | 'dark'
  /** Load brand webfonts (default true). Attr: data-relay-webfont="false". */
  webfont?: boolean
  launcher?: boolean
  position?: 'bottom-right' | 'bottom-left'
  /** Launcher teaser ("optional message" above the bubble). Matches the React
   *  `launcherMessage` prop exactly: a bare string (title only), or
   *  `{ title, subtitle }`. `launcherSubtitle` below is a SEPARATE flat
   *  field kept only so `data-relay-launcher-message` /
   *  `data-relay-launcher-subtitle` (two HTML attributes — an attribute
   *  can't hold a nested object) can still combine into the same shape; in
   *  JS-object form just pass the object directly, same as React. Omit to
   *  use the chatroom's manifest value. */
  launcherMessage?: string | { title: string; subtitle?: string }
  /** @deprecated HTML-attribute-only companion to a string `launcherMessage`
   *  — see the note above. Prefer `launcherMessage: { title, subtitle }` in
   *  JS-object form. */
  launcherSubtitle?: string
  translateLang?: string
  /** Mount INLINE into an existing element instead of the auto-created,
   *  body-appended host that the default floating launcher uses. A CSS
   *  selector string (works from `data-relay-target` too) or an element
   *  reference (JS-object form only). Ignored when `launcher` is true — same
   *  restriction as `height`/`inbox` below: a floating launcher panel is a
   *  fixed-size popup `mount()` owns, not something you place in the page. */
  el?: string | HTMLElement
  /** Inline container height — matches the React `height` prop. Only applies
   *  when `launcher` is false/omitted. */
  height?: string
  /** Adds a back-chevron to the widget that swaps it for the full
   *  conversation list — matches the React `ChatWidget`/`MarketplaceChat`
   *  `inbox` prop, INCLUDING its one limitation: not supported in launcher
   *  mode. (React itself requires a different component, `ChatAppLauncher`,
   *  for a launcher+inbox combination — same scope boundary here.) Tapping a
   *  row opens that conversation; its own back-chevron returns to the list; a
   *  ✕ in the list view returns to this widget's original single-thread
   *  view. Requires `launcher: false`. */
  inbox?: boolean
  /** Open DIRECTLY on the conversation list instead of a single thread —
   *  what you want for a dedicated "Messages" page. `inbox: true` alone only
   *  adds a back-chevron to a single thread, so the list is reachable but
   *  never the landing view; that is the right default for a widget bolted
   *  onto a product page, and the wrong one for a page whose whole job is the
   *  inbox. Implies `inbox`. Requires `launcher: false` (same restriction as
   *  `inbox`), and `profileId` OR `tenantId`.
   *
   *  With `inboxStart`, tapping a row opens that conversation and its own
   *  back-chevron returns to the list. There is no ✕ — the list IS the root
   *  view here, so there is nothing behind it to close back to. */
  inboxStart?: boolean
  /** Inbox scope when `inbox` is set: `'tenant'` (default) lists the user's
   *  threads across ALL your chatrooms; `'profile'` limits it to this one. */
  inboxScope?: 'tenant' | 'profile'
  /** Your business id — lists the visitor's threads across ALL your chatrooms
   *  without naming one, and lets "new conversation" open against your default
   *  chatroom (the server reports it). This is the vanilla equivalent of
   *  React's `<ChatApp tenantId=… />`, which has always accepted a tenant with
   *  no profile; the script tag previously could not express that at all and
   *  hard-required a `profileId`.
   *
   *  Only meaningful with `inboxStart` — a single-thread widget still needs a
   *  `profileId`, because a lone thread has to belong to a specific chatroom. */
  tenantId?: string
}

const DEFAULT_URL = 'wss://api.paramms.com/ws'

function toMountOptions(s: RelaySettings, el: HTMLElement): MountOptions {
  const profileId = s.profileId ?? s.appId
  if (!profileId) throw new Error("Relay: `profileId` is required (your chatroom id, e.g. 'p_...').")
  const subjectId = s.subjectId ?? (s.listingId ? `listing_${s.listingId}` : undefined)
  const launcher = s.launcher ?? true

  // Subject/context card: an explicit nested `subject` wins (it's the most
  // direct match for MountOptions.subject — e.g. what a Liquid/PHP template
  // naturally emits); otherwise build it from whichever flat vocabulary was
  // used — contextTitle (ChatWidget-style), listingTitle (MarketplaceChat-
  // style), or the original subjectTitle — checked in that order.
  const title    = s.contextTitle    ?? s.listingTitle ?? s.subjectTitle
  const subtitle = s.contextSubtitle ?? s.listingMeta   ?? s.subjectMeta
  const status   = s.contextStatus   ?? s.listingStatus ?? s.subjectStatus
  const price    = s.listingPrice    ?? s.subjectPrice
  const builtSubject = title ? {
    title,
    ...(subtitle        ? { subtitle }                                : {}),
    ...(price != null    ? { tags: [`$${price.toLocaleString()}`] }    : {}),
    ...(status           ? { status }                                  : {}),
  } : undefined
  const subject = s.subject ?? builtSubject

  // Display info: an explicit nested `user` wins (matches MountOptions.user
  // directly — what Shopify/WordPress-style server templates naturally emit);
  // otherwise build it from userName/userEmail/userAvatar, falling back to
  // the original flat name/email/avatar.
  const uName   = s.userName   ?? s.name
  const uEmail  = s.userEmail  ?? s.email
  const uAvatar = s.userAvatar ?? s.avatar
  const builtUser = (uName || uEmail || uAvatar) ? {
    ...(uName   ? { name:   uName }   : {}),
    ...(uEmail  ? { email:  uEmail }  : {}),
    ...(uAvatar ? { avatar: uAvatar } : {}),
  } : undefined
  const user = s.user ?? builtUser

  // Launcher teaser: an object form (JS-object install) passes straight
  // through, matching the React `launcherMessage` prop exactly. A bare string
  // combines with the separate `launcherSubtitle` field ONLY needed for
  // `data-relay-*` attributes (an HTML attribute can't hold a nested object).
  const launcherMessage = s.launcherMessage && typeof s.launcherMessage === 'object'
    ? s.launcherMessage
    : s.launcherMessage
      ? (s.launcherSubtitle ? { title: s.launcherMessage, subtitle: s.launcherSubtitle } : s.launcherMessage)
      : undefined

  return {
    el,
    url: s.url ?? DEFAULT_URL,
    profileId,
    ...(s.apiUrl        ? { apiUrl: s.apiUrl }               : {}),
    ...(subjectId       ? { subjectId }                      : {}),
    ...(s.token         ? { token: s.token }                 : {}),
    ...(s.refreshToken  ? { refreshToken: s.refreshToken }   : {}),
    ...(s.userId        ? { userId: s.userId }               : {}),
    ...(subject         ? { subject }                        : {}),
    ...(user            ? { user }                           : {}),
    ...(s.quickReplies  ? { quickReplies: s.quickReplies }   : {}),
    ...(s.i18n          ? { i18n: s.i18n }                   : {}),
    ...(s.features      ? { features: s.features }           : {}),
    ...(s.accent        ? { accent: s.accent }               : {}),
    ...(s.accent2       ? { accent2: s.accent2 }             : {}),
    ...(s.theme         ? { theme: s.theme }                 : {}),
    ...(s.locale        ? { locale: s.locale }               : {}),
    ...(s.webfont === false ? { webfont: false }             : {}),
    ...(s.translateLang ? { translateLang: s.translateLang } : {}),
    ...(launcherMessage ? { launcherMessage }                : {}),
    // Inbox (inline only — matches React exactly, including its one limit:
    // launcher mode has no `inbox`, that's a separate component there too).
    ...(inboxEnabled(s) && !launcher ? { onBack: () => { showInboxList = true; listIsRoot = false; remount(current) } } : {}),
    launcher,
    position: s.position ?? 'bottom-right',
  }
}

let handle: WidgetHandle | null = null
let hostEl: HTMLElement | null = null
/** True when `hostEl` is an element the HOST PAGE owns (via `el`/`data-relay-target`)
 *  rather than one we created — we never remove or take full ownership of it,
 *  only mount into and clear it. */
let externalHost = false
let current: RelaySettings = {}
/** Mirrors React's `showInbox` state: true while the inline widget is showing
 *  the full conversation list (reached via the single thread's back-chevron)
 *  instead of its own single thread. Reset to false on a fresh `boot`;
 *  preserved across `update` (so identify/navigation while browsing the inbox
 *  doesn't silently kick the visitor out of it — matches how ChatApp reacts
 *  to prop changes without unmounting in React). */
let showInboxList = false
/** True when the list is the ROOT view (`inboxStart`) rather than somewhere we
 *  navigated to from a single thread. Drives whether the ✕ ("back to the
 *  original thread") is offered at all — with no thread behind it, a ✕ would
 *  close to nothing. */
let listIsRoot = false

/** Inline inbox is enabled by either flag; `inboxStart` implies `inbox`. */
function inboxEnabled(s: RelaySettings): boolean { return !!(s.inbox || s.inboxStart) }
/** Cleanup for whatever `mountInboxStack` currently has mounted, so switching
 *  back to the single-thread view (or shutting down) leaves the host clean. */
let inboxTeardown: (() => void) | null = null

function ensureHost(s: RelaySettings, launcher: boolean): HTMLElement {
  const wantsExternal = !launcher && !!s.el
  // No longer targeting a page-owned element (launcher mode now, or `el`
  // cleared/unset since the last mount) — release it. We never remove it (we
  // don't own it), just stop treating it as ours so the auto-create path
  // below takes over. Safe to blank its contents here: by the time
  // ensureHost() runs, remount() has already closed whatever was live
  // (handle / inbox stack), so nothing is orphaned mid-connection.
  if (externalHost && !wantsExternal) {
    hostEl!.innerHTML = ''
    hostEl = null
    externalHost = false
  }
  // A target only applies inline — a floating launcher panel is a fixed popup
  // mount() owns, not something placed at a point in the page.
  if (wantsExternal) {
    const resolved = typeof s.el === 'string' ? document.querySelector(s.el) : s.el
    if (resolved instanceof HTMLElement) {
      if (hostEl && hostEl !== resolved && !externalHost) hostEl.remove()   // drop any previously auto-created host
      hostEl = resolved
      externalHost = true
    } else {
      console.error(`[Relay] \`el\` ("${String(s.el)}") did not match any element — falling back to an auto-created host.`)
    }
  }
  if (!hostEl || (!externalHost && !hostEl.isConnected)) {
    hostEl = document.createElement('div')
    hostEl.id = 'relay-widget-root'
    document.body.appendChild(hostEl)
    externalHost = false
  }
  if (!launcher && s.height) {
    hostEl.style.height = s.height
    if (!hostEl.style.width) hostEl.style.width = '100%'
  }
  return hostEl
}

function remount(s: RelaySettings): void {
  try {
    const launcher = s.launcher ?? true
    // Always tear down whatever's currently mounted FIRST, before host
    // resolution — otherwise a host-target/launcher-mode switch can leave a
    // live socket (single-thread OR inbox list/thread) attached to DOM that
    // ensureHost() is about to blank or abandon underneath it.
    inboxTeardown?.(); inboxTeardown = null
    handle?.close(); handle = null

    const host = ensureHost(s, launcher)
    if (inboxEnabled(s) && !launcher && showInboxList) {
      void mountInboxStack(s, host)
      return
    }
    handle = mount(toMountOptions(s, host))
  } catch (e) {
    // Never throw into the host page — a misconfigured embed logs and no-ops.
    console.error('[Relay]', e instanceof Error ? e.message : e)
  }
}

/** The inbox stack (inline, `inbox: true`, back-chevron reached): a list pane
 *  and a thread pane sharing one host, plus a ✕ that returns to the widget's
 *  ORIGINAL single-thread view. This is deliberately the vanilla equivalent
 *  of React's `<ChatApp>` used internally by `ChatWidget`'s `inbox` prop — two
 *  ALREADY-BUILT, already-shared engines (`mount()` for a thread,
 *  `mountChatList()` for the list, both used by the dashboard too) wired
 *  together, not a new chat engine. `mountChatList` is imported dynamically
 *  for code clarity (keeps this module's structure matching its own
 *  `chatlist.ts` boundary) — NOTE this is NOT a bundle-size optimization here:
 *  `vite.embed.config.ts` builds `embed.js` as a single IIFE with no chunk
 *  splitting, so it gets inlined regardless of whether `inbox` is ever used
 *  (confirmed: embed.js grew ~100KB→114KB, ~4KB gzipped, after this change).
 *  A real lazy-load would need a different bundle format for embed.js — out
 *  of scope here, flagged in the roadmap as a minor follow-up. */
async function mountInboxStack(s: RelaySettings, host: HTMLElement): Promise<void> {
  const profileId = s.profileId ?? s.appId
  // A tenant-level inbox needs no chatroom: the list spans all of them and the
  // server reports which one "new conversation" should open against. Matches
  // React's <ChatApp tenantId=… /> exactly.
  if (!profileId && !s.tenantId) {
    console.error('[Relay] `inbox` requires `profileId` or `tenantId`.')
    return
  }

  host.innerHTML = ''
  const wrap = document.createElement('div')
  wrap.style.cssText = 'position:relative;width:100%;height:100%;overflow:hidden;background:#fff'
  const listPane = document.createElement('div')
  listPane.style.cssText = 'position:absolute;inset:0'
  const threadPane = document.createElement('div')
  threadPane.style.cssText = 'position:absolute;inset:0;display:none'
  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.setAttribute('aria-label', resolveStrings(s.locale ?? s.translateLang, s.i18n).close)
  closeBtn.textContent = '✕'
  closeBtn.style.cssText = 'position:absolute;top:8px;right:8px;z-index:20;width:32px;height:32px;border-radius:50%;'
    + 'display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.05);border:none;'
    + 'color:#1c1b1a;font-size:16px;line-height:1;cursor:pointer'
  // ✕ = leave the inbox stack entirely, back to the ORIGINAL single-thread
  // view (mirrors ChatApp's onClose, wired by ChatWidget to setShowInbox(false)).
  closeBtn.onclick = () => { showInboxList = false; remount(current) }
  wrap.append(listPane, threadPane)
  // ...but only when there IS a thread behind the list to go back to. With
  // `inboxStart` the list is the root view, so a ✕ would close to nothing —
  // and with a tenant-only config there is no single thread to construct at
  // all (`toMountOptions` would throw on the missing profileId).
  const canCloseToThread = !listIsRoot && !!profileId
  if (canCloseToThread) wrap.append(closeBtn)
  host.appendChild(wrap)

  let listHandle: import('./chatlist.js').ChatListHandle | null = null
  let threadHandle: WidgetHandle | null = null
  // Set BEFORE the await below: if shutdown() (or a superseding remount())
  // runs while the dynamic import is still in flight, there must already be
  // a way to clean up the synchronously-created `wrap` — otherwise, for an
  // EXTERNAL host (never `.remove()`d), it would sit there orphaned forever.
  // The closure reads `threadHandle`/`listHandle` at CALL time, so it's still
  // correct once they're actually assigned below.
  inboxTeardown = () => { threadHandle?.close(); listHandle?.close(); host.innerHTML = '' }

  // A failed dynamic import used to reject into nothing: the promise had no
  // catch, so the host page got a silent blank box and only an unhandled
  // rejection in the console. Say what happened, in the box the visitor is
  // looking at.
  let mountChatList: typeof import('./chatlist.js').mountChatList
  try {
    ({ mountChatList } = await import('./chatlist.js'))
  } catch (e) {
    console.error('[Relay] could not load the conversation list module.', e)
    if (listPane.isConnected) {
      listPane.style.cssText += ';display:flex;align-items:center;justify-content:center;'
        + 'padding:16px;font:13px system-ui;color:#b91c1c;text-align:center'
      listPane.textContent = 'Could not load conversations.'
    }
    return
  }
  type Entry = import('./chatlist.js').ChatListEntry
  // The dynamic import is async: if the stack was torn down (shutdown, or a
  // subsequent remount already cleared `host`) before it resolved, bail —
  // same guard React's own async effect uses (a `cancelled` flag there).
  if (!listPane.isConnected) return

  const openThread = (entry: Entry): void => {
    // Rows always carry their own chatroom and compose always sets one, so a
    // missing chatroom here means a malformed row rather than a normal state —
    // and with a tenant-only config there is no fallback to reach for. Refuse
    // loudly instead of mounting a thread against nothing.
    const roomId = entry.profileId ?? profileId
    if (!roomId) { console.error('[Relay] conversation row has no chatroom; cannot open it.', entry); return }
    closeBtn.style.display = 'none'   // hidden while a thread is shown — matches React (`onClose && !selected`)
    threadHandle?.close()
    threadHandle = mount({
      el: threadPane,
      url: s.url ?? DEFAULT_URL,
      ...(s.apiUrl ? { apiUrl: s.apiUrl } : {}),
      profileId: roomId,
      // DM rows only open correctly via kind+peerId (their subjectId only
      // resolves for the participant that owns the thread key) — same
      // handling ChatApp itself uses.
      ...(entry.kind === 'direct' && entry.peerId
        ? { kind: 'direct' as const, peerId: entry.peerId }
        : entry.subjectId ? { subjectId: entry.subjectId } : {}),
      ...(s.token ? { token: s.token } : {}),
      ...(s.refreshToken ? { refreshToken: s.refreshToken } : {}),
      ...(s.userId ? { userId: s.userId } : {}),
      ...(s.userId || s.userName || s.userEmail || s.userAvatar ? {
        user: {
          ...(s.userName   ? { name:   s.userName }   : {}),
          ...(s.userEmail  ? { email:  s.userEmail }  : {}),
          ...(s.userAvatar ? { avatar: s.userAvatar } : {}),
        },
      } : {}),
      ...(entry.subjectTitle ? {
        subject: { title: entry.subjectTitle, ...(entry.subjectMeta ? { subtitle: entry.subjectMeta } : {}) },
      } : {}),
      ...(s.accent ? { accent: s.accent } : {}),
      launcher: false,
      // Row's own back-chevron: return to the LIST (not the original single
      // thread — that needs the ✕ above), matching ChatApp's setSelected(null).
      onBack: () => {
        threadHandle?.close(); threadHandle = null
        threadPane.style.display = 'none'
        listPane.style.display = ''
        closeBtn.style.display = ''   // visible again now that the list is showing
        listHandle?.refresh()   // reflects what just happened (read state, preview)
      },
    })
    listPane.style.display = 'none'
    threadPane.style.display = ''
  }

  listHandle = mountChatList({
    el: listPane,
    url: s.url ?? DEFAULT_URL,
    ...(s.apiUrl ? { apiUrl: s.apiUrl } : {}),
    ...(profileId ? { profileId } : {}),
    ...(s.tenantId ? { tenantId: s.tenantId } : {}),
    scope: s.inboxScope ?? 'tenant',
    // Only when the ✕ is actually drawn (see canCloseToThread) — otherwise the
    // reserved gap would be empty space for no reason.
    ...(canCloseToThread ? { reserveCloseSpace: true } : {}),
    onSelect: openThread,
    // ✎ compose: open a fresh thread against this widget's own chatroom when
    // one is configured; with a tenant-only config, against the server-reported
    // default (the tenant's oldest chatroom), exactly like <ChatApp/>. That
    // default only exists after the first successful fetch, so bail quietly
    // until then rather than opening a thread with no chatroom.
    onNewChat: () => {
      const target = profileId ?? listHandle?.defaultProfileId()
      if (!target) return
      openThread({ id: '__new__', profileId: target, state: 'open', updatedAt: Date.now() })
    },
    ...(s.token ? { token: s.token } : {}),
    ...(s.userId ? { userId: s.userId } : {}),
    ...(s.accent ? { accent: s.accent } : {}),
  })
}

/** Mount (or re-mount) with a fresh set of settings. */
function boot(s: RelaySettings): void {
  // `inboxStart` lands ON the list. `inbox` alone still lands on the single
  // thread (the list is behind its back-chevron) — unchanged, because that is
  // what every currently-deployed embed expects.
  listIsRoot = !!(s.inboxStart && !(s.launcher ?? true))
  showInboxList = listIsRoot
  current = { ...s }
  remount(current)
}

/** Merge new settings over the current ones and re-mount. Used for identify
 *  (add a userId after login) and navigation (switch listingId). Preserves
 *  `showInboxList` — identify/navigation shouldn't silently kick a visitor
 *  out of the inbox they're currently browsing. */
function update(s: RelaySettings): void {
  current = { ...current, ...s }
  remount(current)
}

/** Tear the widget down completely (e.g. on logout). */
function shutdown(): void {
  inboxTeardown?.(); inboxTeardown = null
  handle?.close(); handle = null
  if (hostEl && !externalHost) hostEl.remove()
  hostEl = null; externalHost = false
  showInboxList = false
  listIsRoot = false
  current = {}
}

export type RelayCommand = 'boot' | 'update' | 'identify' | 'shutdown'

/** The public command dispatcher exposed as `window.Relay`. */
export function Relay(command: RelayCommand | string, arg?: unknown): void {
  switch (command) {
    case 'boot':     boot((arg ?? {}) as RelaySettings); break
    case 'update':
    case 'identify': update((arg ?? {}) as RelaySettings); break   // identify == update with a userId
    case 'shutdown': shutdown(); break
    default: console.warn('[Relay] unknown command:', command)
  }
}

/** Read settings from a `<script data-relay-app="p_x">` (or
 *  `data-relay-profile-id="p_x"`, same thing) tag — the zero-JavaScript
 *  install: every field here is a plain string/number, so it's exactly what a
 *  server template (WordPress/Shopify/Liquid/PHP/ERB) can drop straight into
 *  an attribute without writing any script. Fields that need a nested object,
 *  an array, or a function (`user`, `subject`, `quickReplies`, `i18n`,
 *  `refreshToken`) have NO attribute form — those need the JS-object install
 *  (`window.relaySettings` / `Relay('boot', ...)`) instead. */
function readDataAttrs(): RelaySettings | null {
  if (typeof document === 'undefined') return null
  const s = (document.querySelector('script[data-relay-app]')
    ?? document.querySelector('script[data-relay-profile-id]')
    // An inbox page names a business, not a chatroom — that tag has neither of
    // the two attributes above, so it needs its own lookup or it is invisible
    // to the zero-JS install path.
    ?? document.querySelector('script[data-relay-tenant-id]')) as HTMLElement | null
  const d = s?.dataset
  const app = d?.['relayApp'] ?? d?.['relayProfileId']
  const tenant = d?.['relayTenantId']
  // A tenant-only tag is valid for an inbox page (no chatroom to name).
  if (!app && !tenant) return null
  const out: RelaySettings = app ? { profileId: app } : {}
  if (tenant) out.tenantId = tenant
  if (d!['relayUser'])    out.userId    = d!['relayUser']
  if (d!['relayToken'])   out.token     = d!['relayToken']
  if (d!['relayListing']) out.listingId = d!['relayListing']
  if (d!['relayAccent'])  out.accent    = d!['relayAccent']
  if (d!['relayAccent2']) out.accent2   = d!['relayAccent2']
  if (d!['relayTheme'])   out.theme     = d!['relayTheme'] as 'auto'|'light'|'dark'
  if (d!['relayLocale'])  out.locale    = d!['relayLocale']
  if (d!['relayWebfont'] === 'false') out.webfont = false
  if (d!['relayUrl'])     out.url       = d!['relayUrl']
  const pos = d!['relayPosition']
  if (pos === 'bottom-left' || pos === 'bottom-right') out.position = pos
  if (d!['relayLauncherMessage'])  out.launcherMessage  = d!['relayLauncherMessage']
  if (d!['relayLauncherSubtitle']) out.launcherSubtitle = d!['relayLauncherSubtitle']
  if (d!['relayLauncher'] === 'false') out.launcher = false
  // Display info — matches userName/userEmail/userAvatar in the JS-object form.
  if (d!['relayUserName'])   out.userName   = d!['relayUserName']
  if (d!['relayUserEmail'])  out.userEmail  = d!['relayUserEmail']
  if (d!['relayUserAvatar']) out.userAvatar = d!['relayUserAvatar']
  // Context/listing card — either vocabulary, matching the JS-object form.
  if (d!['relayContextTitle'])    out.contextTitle    = d!['relayContextTitle']
  if (d!['relayContextSubtitle']) out.contextSubtitle = d!['relayContextSubtitle']
  if (d!['relayContextStatus'])   out.contextStatus   = d!['relayContextStatus']
  if (d!['relayListingTitle'])    out.listingTitle    = d!['relayListingTitle']
  if (d!['relayListingMeta'])     out.listingMeta     = d!['relayListingMeta']
  if (d!['relayListingPrice'])    out.listingPrice    = Number(d!['relayListingPrice'])
  if (d!['relayListingStatus'])   out.listingStatus   = d!['relayListingStatus']
  // Inline placement/sizing/inbox — all plain strings, so all attribute-safe.
  if (d!['relayTarget'])     out.el         = d!['relayTarget']
  if (d!['relayHeight'])     out.height     = d!['relayHeight']
  if (d!['relayInbox'] === 'true')  out.inbox = true
  if (d!['relayInbox'] === 'false') out.inbox = false
  if (d!['relayInboxStart'] === 'true')  out.inboxStart = true
  if (d!['relayInboxStart'] === 'false') out.inboxStart = false
  const scope = d!['relayInboxScope']
  if (scope === 'tenant' || scope === 'profile') out.inboxScope = scope

  // Translation: ISO code of the language to auto-translate INCOMING messages
  // into (shows a 🌐 button per message). The interface and mount path already
  // supported translateLang for React and JS config; the zero-JS script-tag
  // path could not set it at all until this line — a field-parity gap.
  if (d!['relayTranslateLang']) out.translateLang = d!['relayTranslateLang']
  return out
}

// ── Install: take over the loader stub's queue, then replay it ───────────────
// The paste-in loader defines `window.Relay` as a queue so calls made before
// this script finishes loading aren't lost. We swap in the real dispatcher and
// flush anything queued, then auto-boot from `window.relaySettings` OR a
// `data-relay-app` script attribute if the embedder never called boot() — the
// two zero-JS install paths.
type Queue = { q?: IArguments[] }
type Win = typeof window & { Relay?: ((...a: unknown[]) => void) & Queue; relaySettings?: RelaySettings }

if (typeof window !== 'undefined') {
  const w = window as Win
  const queued: IArguments[] = (w.Relay && w.Relay.q) ? w.Relay.q : []
  w.Relay = Relay as ((...a: unknown[]) => void) & Queue

  let explicitBoot = false
  for (const call of queued) {
    const [cmd, arg] = call as unknown as [string, unknown]
    if (cmd === 'boot') explicitBoot = true
    Relay(cmd, arg)
  }
  if (!explicitBoot) {
    const auto = w.relaySettings ?? readDataAttrs()
    if (auto) boot(auto)
  }
}
